import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  EFurnitureStyle,
  FURNITURE_STYLE_PROMPT_LABELS,
  PhotoGenerationConfig,
  ROOM_OPTIONS,
  ROOM_TYPE_PROMPT_LABELS,
  WizardAnswers,
} from 'app/ai/models/wizard.model';
import { firstValueFrom } from 'rxjs';
import {
  extractImageFromResponse,
  toUploadPayload,
} from './gemini-image.utils';
import * as PROMPTS from './gemini-prompts';

interface GeminiResponse {
  candidates?: { content?: { parts?: unknown[] } }[];
  promptFeedback?: { blockReason?: string };
  error?: { message?: string };
}

/**
 * Errore della pipeline AI con l'informazione se ha senso ritentare.
 * `retryable: false` per blocchi di content-policy e 4xx (ritentare non
 * cambierebbe l'esito); `true` per problemi transitori (rete, 429, 5xx).
 */
export class GeminiError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean
  ) {
    super(message);
    this.name = 'GeminiError';
  }
}

@Injectable({ providedIn: 'root' })
export class GeminiService {
  #httpClient = inject(HttpClient);

  /** Numera una lista di istruzioni: ['a','b'] → '1. a\n2. b'. */
  private numbered(steps: readonly string[]): string {
    return steps.map((step, index) => `${index + 1}. ${step}`).join('\n');
  }

  private buildPreserveBlock(allowStructuralChanges: boolean): string {
    if (allowStructuralChanges) {
      return PROMPTS.CAMERA_PRESERVE;
    }
    return `${PROMPTS.CAMERA_PRESERVE}\n${PROMPTS.STRUCTURAL_PRESERVE}`;
  }

  private buildRoomLabel(config?: PhotoGenerationConfig): string {
    if (config?.roomType) {
      return ` of a ${ROOM_TYPE_PROMPT_LABELS[config.roomType]}`;
    }
    return '';
  }

  private buildPhotoConditionsBlock(config?: PhotoGenerationConfig): string {
    if (!config) {
      return '';
    }

    const lines: string[] = [];
    if (config.roomType) {
      const selected = ROOM_OPTIONS[config.roomType].filter((option) =>
        config.selectedOptionIds.includes(option.id)
      );
      for (const option of selected) {
        lines.push(`• ${option.promptInstruction}`);
      }
    }

    const customPrompt = config.customPrompt.trim();
    if (customPrompt) {
      lines.push(`${PROMPTS.USER_REQUEST_PREFIX}${customPrompt}`);
    }

    if (!lines.length) {
      return '';
    }

    return `\n\n${PROMPTS.PHOTO_CONDITIONS_HEADER}\n${lines.join('\n')}`;
  }

  buildPrompt(answers: WizardAnswers, config?: PhotoGenerationConfig): string {
    const { furnitureStyle, removeFurniture, removePersonalEffects } = answers;
    const preserve = this.buildPreserveBlock(
      config?.allowStructuralChanges ?? false
    );
    const photoConditions = this.buildPhotoConditionsBlock(config);
    const roomLabel = this.buildRoomLabel(config);

    if (furnitureStyle === EFurnitureStyle.RIMUOVI_ARREDI) {
      return this.buildRemoveFurniturePrompt({
        removeFurniture: removeFurniture ?? false,
        removePersonalEffects: removePersonalEffects ?? false,
        preserve,
        photoConditions,
        roomLabel,
      });
    }

    const steps = this.numbered(PROMPTS.STAGING_STEPS);
    const styleLabel = FURNITURE_STYLE_PROMPT_LABELS[furnitureStyle];
    const descriptor = PROMPTS.STYLE_DESCRIPTORS[furnitureStyle];

    return `${PROMPTS.STAGING_ROLE_INTENT}
Edit the provided input photo${roomLabel} — do not generate a new image from scratch; transform the existing one.

${preserve}

TRANSFORMATION — restyle the space into the STYLE described below; perform these steps in order:
${steps}

STYLE — ${styleLabel}
${descriptor} Keep all materials and finishes at a ${PROMPTS.PREMIUM_LUXURY}.${photoConditions}

${PROMPTS.IMPROVE_IMAGES}`;
  }

  buildConsistencyFooter(answers: WizardAnswers): string {
    const { furnitureStyle } = answers;

    if (furnitureStyle === EFurnitureStyle.RIMUOVI_ARREDI) {
      return `

IMPORTANT: Maintain full consistency with the rest of the photo series: ${PROMPTS.REMOVE_FURNITURE_STYLE}. Same cleanup quality, same neutral finish level, and same architectural realism across all photos in this listing.`;
    }

    const styleLabel = FURNITURE_STYLE_PROMPT_LABELS[furnitureStyle];
    return `

IMPORTANT: Maintain full consistency with the rest of the photo series: ${styleLabel} interior design, ${PROMPTS.PREMIUM_LUXURY}. Same color palette, same materials, same staging quality as the other photos in this listing.`;
  }

  private buildRemoveFurniturePrompt({
    removeFurniture,
    removePersonalEffects,
    preserve,
    photoConditions,
    roomLabel,
  }: {
    removeFurniture: boolean;
    removePersonalEffects: boolean;
    preserve: string;
    photoConditions: string;
    roomLabel: string;
  }): string {
    const objective = this.buildRemoveFurnitureObjective({
      removeFurniture,
      removePersonalEffects,
    });
    // Sequenza completa numerata: istruzioni dinamiche (1-3) + step fissi.
    // Il finishing (IMPROVE_IMAGES) è appeso come footer, fuori dalla lista.
    const whatToDo = this.numbered([
      ...this.buildRemoveFurnitureInstructions({
        removeFurniture,
        removePersonalEffects,
      }),
      ...PROMPTS.REMOVAL_TAIL_STEPS,
    ]);

    return `${PROMPTS.REMOVAL_ROLE_INTENT}
Edit the provided input photo${roomLabel} — do not generate a new image from scratch; transform the existing one. Your ONLY task is to ${objective}.

${preserve}

${PROMPTS.REMOVAL_SCOPE}

WHAT TO DO:
${whatToDo}${photoConditions}

${PROMPTS.IMPROVE_IMAGES}`;
  }

  private buildRemoveFurnitureObjective({
    removeFurniture,
    removePersonalEffects,
  }: {
    removeFurniture: boolean;
    removePersonalEffects: boolean;
  }): string {
    const { both, furniture, personalEffects, none } =
      PROMPTS.REMOVAL_OBJECTIVES;
    if (removeFurniture && removePersonalEffects) {
      return both;
    }
    if (removeFurniture) {
      return furniture;
    }
    if (removePersonalEffects) {
      return personalEffects;
    }
    return none;
  }

  /** Istruzioni di rimozione (step 1-3) in ordine fisso, scelte dai flag. */
  private buildRemoveFurnitureInstructions({
    removeFurniture,
    removePersonalEffects,
  }: {
    removeFurniture: boolean;
    removePersonalEffects: boolean;
  }): string[] {
    const i = PROMPTS.REMOVAL_INSTRUCTIONS;
    return [
      removePersonalEffects ? i.personalEffectsOn : i.personalEffectsOff,
      removeFurniture ? i.furnitureOn : i.furnitureOff,
      i.keepDecor,
    ];
  }

  buildEditPrompt(userPrompt: string): string {
    return `Generate an edited version of the provided image. Apply this single change: ${userPrompt}

CRITICAL CONSTRAINT: Apply ONLY the modification explicitly requested above. Do NOT change anything else in the image: no other furniture, no colors, no lighting, no materials, no room structure, no framing, no perspective, no architectural elements, no floor, no walls, no ceiling. Every pixel not directly involved in the requested edit must remain identical to the input image.

Output the edited image with framing identical to the input.`;
  }

  async enhanceImage(file: File, prompt: string): Promise<File> {
    const { data: base64, mimeType } = await toUploadPayload(file);

    const body = {
      contents: [
        {
          role: 'user',
          parts: [
            { text: prompt },
            {
              inlineData: { mimeType, data: base64 },
            },
          ],
        },
      ],
    };

    let json: GeminiResponse;
    try {
      json = await firstValueFrom(
        this.#httpClient.post<GeminiResponse>(
          `${environment.apiUrl}/ai-image-editor`,
          body
        )
      );
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      const apiMessage =
        (httpError.error as GeminiResponse | undefined)?.error?.message ||
        httpError.message ||
        'Gemini request failed';
      throw new GeminiError(
        apiMessage,
        this.isRetryableStatus(httpError.status)
      );
    }

    if (json.promptFeedback?.blockReason) {
      // Blocco di content-policy: ritentare con lo stesso input non aiuta.
      throw new GeminiError(
        `Gemini blocked: ${json.promptFeedback.blockReason}`,
        false
      );
    }

    const blob = extractImageFromResponse(json);
    return new File([blob], file.name, { type: blob.type });
  }

  /**
   * 0/408/429/5xx sono transitori (rete, timeout, rate-limit, server) → vale
   * la pena ritentare. Gli altri 4xx sono errori di richiesta → terminali.
   */
  private isRetryableStatus(status: number): boolean {
    return status === 0 || status === 408 || status === 429 || status >= 500;
  }
}
